// src/platform/core/eventBus.js

class EventBus {
  constructor() {
    this.listeners = new Map()
  }

  /**
   * Subscribe to an event.
   *
   * Returns an unsubscribe function.
   */
  on(eventName, listener) {
    if (!eventName) {
      throw new Error(
        '[EventBus] Event name is required.'
      )
    }

    if (typeof listener !== 'function') {
      throw new Error(
        '[EventBus] Listener must be a function.'
      )
    }

    if (!this.listeners.has(eventName)) {
      this.listeners.set(
        eventName,
        new Set()
      )
    }

    const listeners =
      this.listeners.get(eventName)

    listeners.add(listener)

    return () => {
      this.off(
        eventName,
        listener
      )
    }
  }

  /**
   * Subscribe to an event once.
   */
  once(eventName, listener) {
    if (typeof listener !== 'function') {
      throw new Error(
        '[EventBus] Listener must be a function.'
      )
    }

    const unsubscribe =
      this.on(
        eventName,
        payload => {
          unsubscribe()
          listener(payload)
        }
      )

    return unsubscribe
  }

  /**
   * Remove a specific listener.
   */
  off(eventName, listener) {
    const listeners =
      this.listeners.get(eventName)

    if (!listeners) {
      return false
    }

    const removed =
      listeners.delete(listener)

    if (listeners.size === 0) {
      this.listeners.delete(
        eventName
      )
    }

    return removed
  }

  /**
   * Emit an event.
   */
  emit(eventName, payload = null) {
    const listeners =
      this.listeners.get(eventName)

    if (!listeners) {
      return
    }

    /*
     * Copy first so a listener can safely
     * unsubscribe while events are being emitted.
     */
    const snapshot = [
      ...listeners
    ]

    snapshot.forEach(listener => {
      try {
        listener(payload)
      } catch (error) {
        console.error(
          `[EventBus] Listener failed for "${eventName}":`,
          error
        )
      }
    })
  }

  /**
   * Remove every listener for one event.
   */
  clear(eventName = null) {
    if (eventName) {
      this.listeners.delete(
        eventName
      )
      return
    }

    this.listeners.clear()
  }

  /**
   * Check whether an event currently has listeners.
   */
  hasListeners(eventName) {
    return Boolean(
      this.listeners.get(eventName)?.size
    )
  }

  /**
   * Number of active listeners for an event.
   */
  listenerCount(eventName) {
    return (
      this.listeners.get(eventName)?.size ||
      0
    )
  }
}

const eventBus = new EventBus()

export default eventBus